import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  Download,
  Database,
  MessageSquare,
  Image as ImageIcon,
  Zap,
  Scale as ScaleIcon,
} from 'lucide-react';
import FileUpload from './FileUpload';
import KnowledgeBase from './KnowledgeBase';
import DesignChat from './DesignChat';
import VisualSuggestionsTab from './VisualSuggestionsTab';
import ImageUpscale from './ImageUpscale';
import QAComparison from './QAComparison';
import CombinedAnalysisWorkspace from './CombinedAnalysisWorkspace';
import { ProgressIndicator } from './ProgressIndicator';

import { withBackend } from '../utils/api';
import FileParser from '../services/fileParser';
import ExportService from '../services/exportService';
import KnowledgeService from '../services/knowledgeService';
import ChatService from '../services/chatService';
import TopazService from '../services/topazService';
import { generateSlideThumbnail, uploadThumbnailToSupabase } from '../utils/thumbnailGenerator';
import { supabaseHelpers } from '../lib/supabase';

// Animated dots component for loading screen (typewriter effect)
function AnimatedDots() {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return <span className="inline-block w-8 text-left">{dots}</span>;
}

const DesignAnalysisTool = ({ onBackToHub, project, hideHeader = false, initialAnalysis = null, onAnalysisComplete, initialFile = null }) => {
  console.log('🎬 DesignAnalysisTool props - project:', project);
  console.log('🎬 DesignAnalysisTool props - initialAnalysis:', initialAnalysis);
  const [uploadedFile, setUploadedFile] = useState(initialFile || project?.uploadedFile || null);
  const [analysis, setAnalysis] = useState(initialAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [error, setError] = useState(null);
  const hasTriggeredAutoAnalysis = React.useRef(false);

  const projectAudienceInfo = project?.uploadMetadata?.audienceInfo;

  const openAiKey = useMemo(() => process.env.REACT_APP_OPENAI_API_KEY || '', []);
  const [topazApiKey, setTopazApiKey] = useState(process.env.REACT_APP_TOPAZ_API_KEY || '');

  const [knowledgeService] = useState(() => new KnowledgeService());
  const [knowledgeStats, setKnowledgeStats] = useState(knowledgeService.getStats());
  const [chatService, setChatService] = useState(() => (openAiKey ? new ChatService(openAiKey, knowledgeService) : null));
  const [topazService, setTopazService] = useState(null);

  const fileParser = useMemo(() => new FileParser(), []);
  const exportService = useMemo(() => new ExportService(), []);

  useEffect(() => {
    if (openAiKey) {
      setChatService(new ChatService(openAiKey, knowledgeService));
    } else {
      setChatService(null);
    }
  }, [openAiKey, knowledgeService]);

  // Auto-trigger analysis if we have a file from upload but no analysis yet
  useEffect(() => {
    if (initialFile && !initialAnalysis && !analysis && !loading && openAiKey && !hasTriggeredAutoAnalysis.current) {
      hasTriggeredAutoAnalysis.current = true;
      setUploadedFile(initialFile);
      console.log('🎬 Auto-triggering analysis with audienceInfo:', projectAudienceInfo);
      handleFileUpload(initialFile, projectAudienceInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile, initialAnalysis, analysis, loading, openAiKey, projectAudienceInfo]);

  useEffect(() => {
    if (topazApiKey) {
      setTopazService(new TopazService(topazApiKey));
    } else {
      setTopazService(null);
    }
  }, [topazApiKey]);

  useEffect(() => {
    setKnowledgeStats(knowledgeService.getStats());
  }, [knowledgeService]);

  useEffect(() => {
    if (initialAnalysis) {
      setAnalysis(initialAnalysis);
      if (initialAnalysis.uploadedFileName) {
        setUploadedFile((prev) => prev || { name: initialAnalysis.uploadedFileName });
      }
    }
  }, [initialAnalysis]);

  const handleFileUpload = useCallback(async (file, audienceData) => {
    console.log('🎬 handleFileUpload called with audienceData:', audienceData);
    console.log('🎬 hasVideos value:', audienceData?.hasVideos);
    console.log('🎬 fastAnalysis value:', audienceData?.fastAnalysis);
    setUploadedFile(file);
    setLoading(true);
    setAnalysis(null);
    setError(null);

    // Initialize progress steps
    const steps = [
      { id: 'init', label: 'Initializing', description: 'Preparing for analysis', status: 'in-progress' },
      { id: 'parse', label: 'Parsing file', description: 'Extracting slides and content', status: 'pending' },
      { id: 'context', label: 'Building context', description: 'Gathering knowledge base', status: 'pending' },
      { id: 'analyze', label: 'AI Analysis', description: 'Analyzing with Design Compass methodology (Large presentations may take 5-6 minutes)', status: 'pending' },
      { id: 'finalize', label: 'Finalizing', description: 'Preparing results', status: 'pending' },
    ];
    setProgressSteps(steps);

    try {
      if (!openAiKey) {
        throw new Error('OpenAI API key is not configured.');
      }

      // Update to parsing step
      setProgressSteps(prev => prev.map(s =>
        s.id === 'init' ? { ...s, status: 'completed' } :
          s.id === 'parse' ? { ...s, status: 'in-progress' } : s
      ));
      const parsedContent = await fileParser.parseFile(file, {
        hasVideos: audienceData?.hasVideos,
        fastAnalysis: audienceData?.fastAnalysis
      });

      // Update to context step
      setProgressSteps(prev => prev.map(s =>
        s.id === 'parse' ? { ...s, status: 'completed' } :
          s.id === 'context' ? { ...s, status: 'in-progress' } : s
      ));
      const allDocs = knowledgeService.getAllDocuments();
      const companyContext = allDocs.map((doc) => `${doc.name}: ${doc.content}`).join('\n\n');

      const payloadContent = {
        slide_count: parsedContent.slideCount ?? parsedContent.slide_count ?? null,
        text_content: parsedContent.textContent ?? parsedContent.text_content ?? '',
        metadata: {
          colors: parsedContent.metadata?.colors || [],
          fonts: parsedContent.metadata?.fonts || [],
          has_images: parsedContent.metadata?.hasImages ?? parsedContent.metadata?.has_images ?? false,
          bullet_point_count: parsedContent.metadata?.bulletPointCount ?? parsedContent.metadata?.bullet_point_count ?? 0,
        },
      };

      // Update to analyze step
      setProgressSteps(prev => prev.map(s =>
        s.id === 'context' ? { ...s, status: 'completed' } :
          s.id === 'analyze' ? { ...s, status: 'in-progress' } : s
      ));

      const response = await fetch(withBackend('/analyze'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: openAiKey,
          content: payloadContent,
          company_context: companyContext,
          audience_info: audienceData,
          slides_data: parsedContent.slides || [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Design analysis failed');
      }

      const designAnalysis = await response.json();

      console.log('🔍 DEBUG: Backend response slides:', designAnalysis.slides);
      console.log('🔍 DEBUG: First slide has recommendations?', designAnalysis.slides?.[0]?.recommendations);

      // Only use parsedContent.slides if backend didn't return slides
      if (!designAnalysis.slides && parsedContent.slides) {
        designAnalysis.slides = parsedContent.slides;
      }

      console.log('🔍 DEBUG: Final slides after merge:', designAnalysis.slides);
      console.log('🔍 DEBUG: First slide recommendations:', designAnalysis.slides?.[0]?.recommendations);
      if (parsedContent.metadata) {
        designAnalysis.metadata = parsedContent.metadata;
      }
      designAnalysis.uploadedFileName = file.name;
      designAnalysis.parsedContent = parsedContent;
      designAnalysis.companyContext = companyContext;
      designAnalysis.audienceInfo = audienceData;

      // Update to finalize step
      setProgressSteps(prev => prev.map(s =>
        s.id === 'analyze' ? { ...s, status: 'completed' } :
          s.id === 'finalize' ? { ...s, status: 'in-progress' } : s
      ));
      console.log('💾 Setting analysis state with slides:', designAnalysis.slides);
      console.log('💾 designAnalysis.slides length:', designAnalysis.slides?.length);
      setAnalysis(designAnalysis);

      // Generate thumbnail for FIRST SLIDE ONLY (client-side)
      let thumbnailUrl = null;
      if (designAnalysis.slides && designAnalysis.slides.length > 0 && project?.id) {
        try {
          console.log('📸 Generating client-side thumbnail for first slide...');
          const firstSlide = designAnalysis.slides[0];
          const thumbnailBase64 = await generateSlideThumbnail(firstSlide);

          // Upload to Supabase
          console.log('☁️ Uploading thumbnail to Supabase...');
          thumbnailUrl = await uploadThumbnailToSupabase(thumbnailBase64, project.id, 1);

          if (thumbnailUrl) {
            console.log('✅ Thumbnail uploaded successfully:', thumbnailUrl);
            // Update project with thumbnail
            await supabaseHelpers.updateProject(project.id, {
              thumbnail_url: thumbnailUrl
            });
          } else {
            console.warn('⚠️ Thumbnail upload failed, using base64 fallback');
            thumbnailUrl = thumbnailBase64; // Fallback to base64
          }
        } catch (thumbnailError) {
          console.error('❌ Error generating thumbnail:', thumbnailError);
          // Continue without thumbnail - not critical
        }
      }

      // Complete all steps
      setProgressSteps(prev => prev.map(s => ({ ...s, status: 'completed' })));

      if (typeof onAnalysisComplete === 'function') {
        onAnalysisComplete({
          analysis: designAnalysis,
          fileName: file.name,
          audience: audienceData,
          parsedContent,
          companyContext,
          thumbnailUrl, // Pass thumbnail URL
        });
      }
    } catch (uploadError) {
      console.error('Analysis error:', uploadError);
      setError(uploadError.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [openAiKey, knowledgeService, fileParser, exportService, onAnalysisComplete]);

  const determineDocumentType = (fileName) => {
    const name = fileName.toLowerCase();
    if (name.includes('design compass') || name.includes('presentation compass')) {
      return 'framework';
    }
    if (name.includes('brand') || name.includes('style')) {
      return 'brand';
    }
    if (name.includes('template')) {
      return 'template';
    }
    if (name.includes('guideline')) {
      return 'guideline';
    }
    return 'general';
  };

  const handleAddCompanyDocuments = async (files) => {
    for (const file of files) {
      try {
        const content = await fileParser.parseFile(file);
        knowledgeService.addDocument({
          name: file.name,
          content: content.textContent,
          size: file.size,
          type: determineDocumentType(file.name),
        });
      } catch (docError) {
        console.error('Error parsing company document:', docError);
        knowledgeService.addDocument({
          name: file.name,
          content: `Document: ${file.name} (${Math.round(file.size / 1024)}KB) - Could not parse content`,
          size: file.size,
          type: 'general',
        });
      }
    }
    setKnowledgeStats(knowledgeService.getStats());
  };

  const handleRemoveCompanyDocument = (id) => {
    knowledgeService.removeDocument(id);
    setKnowledgeStats(knowledgeService.getStats());
  };

  const handleExportPDF = () => {
    if (!analysis) return;
    const fileName = `design-compass-brief-${uploadedFile?.name || 'report'}.pdf`;
    exportService.exportAnalysisToPDF(analysis, fileName);
  };

  const handleExportJSON = () => {
    if (!analysis) return;
    const fileName = `design-compass-analysis-${uploadedFile?.name || 'report'}.json`;
    exportService.exportAnalysisToJSON(analysis, fileName);
  };

  const handleExportClientQuestions = () => {
    if (!analysis) return;
    const fileName = `client-questions-${uploadedFile?.name || 'project'}.txt`;
    exportService.exportClientQuestions(analysis, fileName);
  };

  const handleExportDesignBrief = () => {
    if (!analysis) return;
    const fileName = `design-brief-${uploadedFile?.name || 'project'}.txt`;
    exportService.exportDesignBrief(analysis, fileName);
  };

  const handleDownloadBlueprint = async () => {
    if (!analysis) {
      return;
    }

    try {
      setLoading(true);

      const requestData = {
        analysis,
        slides: analysis.slides || [],
      };

      const response = await fetch(withBackend('/generate-blueprint'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Blueprint generation failed');
      }

      const result = await response.json();
      if (result.success && result.blueprint_data) {
        const byteCharacters = atob(result.blueprint_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error('Invalid response from blueprint service');
      }
    } catch (blueprintError) {
      console.error('Blueprint download error:', blueprintError);
      setError(blueprintError.message);
    } finally {
      setLoading(false);
    }
  };

  const renderHeader = () => (
    <header className="border-b border-surface-02 bg-brand-navy/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-mint/10 text-brand-mint">
            <Sparkles className="h-6 w-6" />
          </span>
          <div className="space-y-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">wizardly.ai</p>
              <h1 className="text-2xl font-semibold text-text-primary">AI Presentation Assistant</h1>
            </div>
            {project?.name && (
              <p className="text-xs text-text-muted">Active project: {project.name}</p>
            )}
            {analysis && (
              <p className="text-xs text-text-muted">Analysis generated {new Date().toLocaleString()}</p>
            )}
            {onBackToHub && (
              <button
                type="button"
                onClick={onBackToHub}
                className="inline-flex items-center gap-2 rounded-lg border border-surface-02 px-3 py-1.5 text-xs text-text-muted hover:border-brand-mint/60 hover:text-text-primary"
              >
                Back to Project Hub
              </button>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-xl border border-surface-02 bg-surface-01/60 px-4 py-2">
            <Zap className="h-4 w-4 text-text-muted" />
            <input
              type="password"
              placeholder="Topaz API key (optional)"
              value={topazApiKey}
              onChange={(event) => setTopazApiKey(event.target.value)}
              className="w-full bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
            />
            {topazApiKey && <span className="text-xs text-brand-mint">Ready</span>}
          </div>
          {knowledgeStats?.hasFramework && (
            <span className="rounded-full border border-brand-mint/40 bg-brand-mint/10 px-3 py-1 text-xs font-medium text-brand-mint">
              Framework active
            </span>
          )}
        </div>
      </div>
    </header>
  );

  const renderUploadView = () => (
    <div className="mx-auto max-w-5xl space-y-12 px-6 py-12">
      <section className="rounded-3xl border border-border bg-card/90 p-8 shadow-lg transition-colors dark:bg-surface-01/80">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground">Upload a presentation</h2>
          <p className="text-sm text-muted-foreground">Connect your deck and optional audience context to generate AI insights.</p>
        </div>
        <FileUpload
          onFileUpload={handleFileUpload}
          loading={loading}
          error={error}
          uploadedFile={uploadedFile}
        />
      </section>

      <section className="rounded-3xl border border-border bg-card/90 p-8 shadow-sm transition-colors dark:bg-surface-02/60">
        <div className="mb-6 flex items-center gap-3">
          <Database className="h-5 w-5 text-brand-mint" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Company knowledge</h3>
            <p className="text-sm text-muted-foreground">Upload brand frameworks, style guides, and references.</p>
          </div>
        </div>
        <KnowledgeBase
          companyDocs={knowledgeService.getAllDocuments()}
          onAddDocuments={handleAddCompanyDocuments}
          onRemoveDocument={handleRemoveCompanyDocument}
          knowledgeStats={knowledgeStats}
        />
      </section>
    </div>
  );

  const renderSupportTools = () => (
    <section className="space-y-6">
      <div className="rounded-2xl bg-surface-01 px-6 py-6">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-brand-mint" />
          <h4 className="text-sm font-semibold text-text-primary">Additional exports</h4>
        </div>
        <p className="mt-2 text-xs text-text-muted">Download alternative formats for collaboration or documentation.</p>
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <button
            type="button"
            onClick={handleExportJSON}
            className="rounded-lg border border-surface-02 bg-surface-02/60 px-3 py-2 text-text-muted hover:border-brand-mint/60 hover:text-text-primary"
          >
            JSON export
          </button>
          <button
            type="button"
            onClick={handleExportClientQuestions}
            className="rounded-lg border border-surface-02 bg-surface-02/60 px-3 py-2 text-text-muted hover:border-brand-mint/60 hover:text-text-primary"
          >
            Client questions
          </button>
          <button
            type="button"
            onClick={handleExportDesignBrief}
            className="rounded-lg border border-surface-02 bg-surface-02/60 px-3 py-2 text-text-muted hover:border-brand-mint/60 hover:text-text-primary"
          >
            Design brief
          </button>
        </div>
      </div>

      <details className="rounded-2xl bg-surface-01 px-6 py-6" open>
        <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-text-primary">
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-mint" /> Visual suggestions
          </span>
          <span className="text-xs text-text-muted">Click to collapse</span>
        </summary>
        <div className="mt-6 rounded-xl bg-white p-6 text-gray-900">
          <VisualSuggestionsTab analysis={analysis} apiKey={openAiKey} uploadedFileName={uploadedFile?.name} />
        </div>
      </details>

      <details className="rounded-2xl bg-surface-01 px-6 py-6">
        <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-text-primary">
          <span className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-brand-mint" /> Design assistant chat
          </span>
          <span className="text-xs text-text-muted">Click to expand</span>
        </summary>
        <div className="mt-6 rounded-xl bg-white p-6 text-gray-900">
          {chatService ? (
            <DesignChat
              chatService={chatService}
              presentationAnalysis={analysis}
              uploadedFileName={uploadedFile?.name}
              knowledgeStats={knowledgeStats}
            />
          ) : (
            <p className="text-sm text-gray-600">Connect to the backend to enable the assistant.</p>
          )}
        </div>
      </details>

      <details className="rounded-2xl bg-surface-01 px-6 py-6">
        <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-text-primary">
          <span className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-brand-mint" /> Image upscaling
          </span>
          <span className="text-xs text-text-muted">Click to expand</span>
        </summary>
        <div className="mt-6 rounded-xl bg-white p-6 text-gray-900">
          <ImageUpscale topazService={topazService} />
        </div>
      </details>

      <details className="rounded-2xl bg-surface-01 px-6 py-6">
        <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-text-primary">
          <span className="flex items-center gap-2">
            <ScaleIcon className="h-4 w-4 text-brand-mint" /> QA comparison
          </span>
          <span className="text-xs text-text-muted">Click to expand</span>
        </summary>
        <div className="mt-6 rounded-xl bg-white p-6 text-gray-900">
          <QAComparison apiKey={openAiKey} />
        </div>
      </details>
    </section>
  );

  return (
    <div className={`${hideHeader ? '' : 'min-h-screen'} bg-brand-navy text-text-primary relative`}>
      {!hideHeader && renderHeader()}

      {/* Loading Overlay */}
      {loading && progressSteps.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy">
          <ProgressIndicator
            title="Analyzing Presentation"
            steps={progressSteps}
            currentStep={progressSteps.find(s => s.status === 'in-progress')?.id}
            showDetails={true}
          />
          {/* Animated Loading Text - Bottom Right */}
          <div className="fixed bottom-8 right-8 text-text-primary/60 text-sm font-medium tracking-wide">
            GOOD THINGS COME TO THOSE THAT WAIT<AnimatedDots />
          </div>
        </div>
      )}
      {!analysis ? (
        renderUploadView()
      ) : (
        <>
          {console.log('📊 RENDER - analysis object:', analysis)}
          {console.log('📊 RENDER - analysis.slides:', analysis.slides)}
          {console.log('📊 RENDER - analysis.slides length:', analysis.slides?.length)}
          {console.log('📊 RENDER - First slide recommendations:', analysis.slides?.[0]?.recommendations)}
          <CombinedAnalysisWorkspace
            analysis={analysis}
            slides={analysis.slides || []}
            uploadedFile={uploadedFile || project}
            uploadedFileName={uploadedFile?.name || project?.name}
            onExportReport={handleExportPDF}
            onExportJSON={handleExportJSON}
            onExportQuestions={handleExportClientQuestions}
            onExportDesignBrief={handleExportDesignBrief}
            onDownloadBlueprint={handleDownloadBlueprint}
            onShare={() => window.alert('Share links coming soon')}
            projectId={project?.id}
          >
            {renderSupportTools()}
          </CombinedAnalysisWorkspace>
        </>
      )}
    </div>
  );
};

export default DesignAnalysisTool;






